import React from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  BookOpenText, Inbox, GavelIcon, ScrollText, Feather, ShieldCheck,
  HelpCircle, LogOut, Pause, Play, Settings2, LayoutDashboard, BookMarked, Key, Newspaper, Mail, Send, MessageSquareReply, TrendingUp, FileEdit,
  Menu, X, ExternalLink,
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const NAV = [
  { to: "/dashboard", label: "Morning Summary", icon: LayoutDashboard, end: true },
  { to: "/dashboard/evening", label: "Evening Review", icon: MessageSquareReply },
  { to: "/dashboard/daily-brief", label: "Daily Brief", icon: Newspaper },
  { to: "/dashboard/newsletter", label: "Newsletter", icon: Mail },
  { to: "/dashboard/substack", label: "Substack", icon: Send },
  { to: "/dashboard/growth", label: "Growth", icon: TrendingUp },
  { to: "/dashboard/inbox", label: "Filtered Replies", icon: Inbox },
  { to: "/dashboard/approvals", label: "Approvals", icon: GavelIcon },
  { to: "/dashboard/tier3", label: "Tier-3 Questions", icon: HelpCircle },
  { to: "/dashboard/platforms", label: "Platforms", icon: BookMarked },
  { to: "/dashboard/compose", label: "Compose", icon: Feather },
  { to: "/dashboard/articles", label: "Articles", icon: ScrollText },
  { to: "/dashboard/home-content", label: "Home copy", icon: FileEdit },
  { to: "/dashboard/editors", label: "Editors", icon: ScrollText },
  { to: "/dashboard/decision-log", label: "Decision Log", icon: BookOpenText },
  { to: "/dashboard/settings", label: "Settings", icon: Settings2 },
];

export default function Layout({ children }) {
  const { author, logout } = useAuth();
  const [paused, setPaused] = React.useState(false);
  const [openPause, setOpenPause] = React.useState(false);
  const [confirm, setConfirm] = React.useState("");
  const [recoveryCount, setRecoveryCount] = React.useState(null);
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const navigate = useNavigate();

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/emergency/status");
      setPaused(!!data.paused);
    } catch (_e) {}
  };
  const fetchRecovery = async () => {
    try {
      const { data } = await api.get("/settings");
      setRecoveryCount(data.recovery_codes_count ?? 0);
    } catch (_e) {}
  };
  React.useEffect(() => { fetchStatus(); fetchRecovery(); }, []);

  const togglePause = async () => {
    if (paused) {
      await api.post("/emergency/resume");
      toast.success("Agents resumed.");
      setPaused(false);
    } else {
      if (confirm.trim().toUpperCase() !== "PAUSE") {
        toast.error("Type PAUSE to confirm.");
        return;
      }
      await api.post("/emergency/pause");
      toast.success("All outgoing activity halted.");
      setPaused(true);
      setOpenPause(false);
      setConfirm("");
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="app-layout">
      {/* Sidebar */}
      <aside className="w-64 border-r hidden md:flex flex-col"
             style={{ borderColor: "var(--elle-border)", background: "var(--elle-bg)" }}>
        <div className="px-6 py-8 hairline">
          <div className="flex items-center gap-2">
            <span className="lamp-dot" />
            <span className="overline">Sanctuary</span>
          </div>
          <h1 className="font-display text-2xl mt-3 leading-tight" style={{ color: "var(--elle-ink)" }}>
            The Boggin<br/>Chronicles
          </h1>
          <p className="text-xs mt-2 font-mono-elle" style={{ color: "var(--elle-ink-dim)" }}>
            Elle · Yorkshire
          </p>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-[var(--elle-surface-2)] text-[var(--elle-ink)] border-l-2 border-[var(--elle-amber)]"
                    : "text-[var(--elle-ink-dim)] hover:bg-[var(--elle-surface)] hover:text-[var(--elle-ink)] border-l-2 border-transparent"
                }`
              }
            >
              <Icon size={16} strokeWidth={1.5} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-6 py-4 hairline" style={{ borderTop: "1px solid var(--elle-border)" }}>
          <div className="text-xs font-mono-elle" style={{ color: "var(--elle-ink-faint)" }}>
            Signed in as
          </div>
          <div className="text-sm mt-1">{author?.display_name || "Elle"}</div>
          <a href="/" target="_blank" rel="noopener noreferrer"
                data-testid="desktop-view-public-site"
                className="mt-3 flex items-center gap-2 text-xs hover:text-[var(--elle-amber)] transition-colors"
                style={{ color: "var(--elle-ink-dim)" }}>
            <ExternalLink size={12}/> View public site
          </a>
          <button onClick={logout} data-testid="logout-btn"
                  className="mt-2 inline-flex items-center gap-2 text-xs hover:text-[var(--elle-amber)] transition-colors"
                  style={{ color: "var(--elle-ink-dim)" }}>
            <LogOut size={12}/> sign out
          </button>
        </div>
      </aside>

      {/* Mobile drawer nav */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-40 md:hidden" data-testid="mobile-nav-overlay">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileNavOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 flex flex-col"
                 style={{ borderRight: "1px solid var(--elle-border)", background: "var(--elle-bg)" }}>
            <div className="px-5 py-5 flex items-center justify-between hairline">
              <div className="flex items-center gap-2">
                <span className="lamp-dot" />
                <span className="overline">Sanctuary</span>
              </div>
              <button data-testid="mobile-nav-close" onClick={() => setMobileNavOpen(false)}
                      className="text-[var(--elle-ink-dim)] hover:text-[var(--elle-ink)]">
                <X size={18}/>
              </button>
            </div>
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
              {NAV.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={() => setMobileNavOpen(false)}
                  data-testid={`mobile-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                      isActive
                        ? "bg-[var(--elle-surface-2)] text-[var(--elle-ink)] border-l-2 border-[var(--elle-amber)]"
                        : "text-[var(--elle-ink-dim)] hover:bg-[var(--elle-surface)] hover:text-[var(--elle-ink)] border-l-2 border-transparent"
                    }`
                  }
                >
                  <Icon size={16} strokeWidth={1.5} />
                  <span>{label}</span>
                </NavLink>
              ))}
            </nav>
            <div className="px-5 py-4 hairline" style={{ borderTop: "1px solid var(--elle-border)" }}>
              <a href="/" target="_blank" rel="noopener noreferrer"
                    onClick={() => setMobileNavOpen(false)}
                    className="flex items-center gap-2 text-xs hover:text-[var(--elle-amber)] transition-colors mb-3"
                    style={{ color: "var(--elle-ink-dim)" }}>
                <ExternalLink size={12}/> View public site
              </a>
              <button onClick={logout}
                      className="inline-flex items-center gap-2 text-xs hover:text-[var(--elle-amber)] transition-colors"
                      style={{ color: "var(--elle-ink-dim)" }}>
                <LogOut size={12}/> sign out
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Top bar with emergency pause */}
        <header className="hairline flex items-center justify-between px-6 md:px-10 py-4 sticky top-0 z-20"
                style={{ background: "rgba(11,14,20,0.85)", backdropFilter: "blur(8px)" }}>
          <div className="md:hidden flex items-center gap-3">
            <button data-testid="mobile-nav-open" onClick={() => setMobileNavOpen(true)}
                    className="text-[var(--elle-ink)] p-1">
              <Menu size={20}/>
            </button>
            <h1 className="font-display text-xl">Sanctuary</h1>
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noopener noreferrer"
                  data-testid="view-public-site-link"
                  title="View public site (new tab)"
                  className="hidden md:inline-flex items-center gap-1.5 text-xs font-mono-elle hover:text-[var(--elle-amber)] transition-colors"
                  style={{ color: "var(--elle-ink-dim)" }}>
              <ExternalLink size={12}/> View site
            </a>
            <div className="flex items-center gap-2 text-xs font-mono-elle"
                 style={{ color: paused ? "var(--elle-oxblood-bright)" : "var(--elle-ink-dim)" }}>
              <ShieldCheck size={14} />
              {paused ? "PAUSED" : "ACTIVE"}
            </div>
            {paused ? (
              <Button data-testid="resume-btn" onClick={togglePause}
                      className="btn-amber rounded-sm">
                <Play size={14} className="mr-2"/> Resume agents
              </Button>
            ) : (
              <Dialog open={openPause} onOpenChange={setOpenPause}>
                <DialogTrigger asChild>
                  <Button data-testid="emergency-pause-btn" className="btn-oxblood rounded-sm">
                    <Pause size={14} className="mr-2"/> Emergency pause
                  </Button>
                </DialogTrigger>
                <DialogContent className="elle-surface border"
                               style={{ borderColor: "var(--elle-oxblood)", color: "var(--elle-ink)" }}>
                  <DialogHeader>
                    <DialogTitle className="font-display text-2xl tier-b">Halt all outgoing activity</DialogTitle>
                    <DialogDescription style={{ color: "var(--elle-ink-dim)" }}>
                      No posts, no replies, no scheduling — on any platform — until you lift the pause.
                      Agents continue to monitor and filter incoming content quietly.
                    </DialogDescription>
                  </DialogHeader>
                  <div>
                    <label className="overline block mb-2" style={{ color: "var(--elle-oxblood-bright)" }}>
                      Type PAUSE to confirm
                    </label>
                    <Input data-testid="pause-confirm-input"
                           value={confirm} onChange={(e)=>setConfirm(e.target.value)}
                           placeholder="PAUSE" className="font-mono-elle"/>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={()=>setOpenPause(false)} className="btn-ghost rounded-sm">
                      Cancel
                    </Button>
                    <Button data-testid="pause-confirm-btn" onClick={togglePause} className="btn-oxblood rounded-sm">
                      Halt everything
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </header>
        <div className="px-6 md:px-10 py-8 md:py-12">
          {recoveryCount === 0 && (
            <div data-testid="recovery-nudge"
                 className="mb-8 p-5 rounded-sm flex items-start gap-3"
                 style={{ border: "1px solid var(--elle-oxblood-bright)",
                          background: "rgba(139,42,42,0.10)" }}>
              <Key size={18} className="mt-0.5 shrink-0" style={{ color: "var(--elle-oxblood-bright)" }}/>
              <div className="flex-1">
                <div className="overline" style={{ color: "var(--elle-oxblood-bright)" }}>
                  Do this before you redeploy
                </div>
                <div className="mt-2 text-sm" style={{ color: "var(--elle-ink)" }}>
                  You haven't generated account recovery codes yet. Without them, a fresh
                  deployment or a database reset will lock you out — there is no email-based
                  password reset. Ten single-use codes — shown once, then gone — are the only
                  way back in. Save them in a password manager. Takes ten seconds.
                </div>
              </div>
              <Link to="/dashboard/settings" data-testid="recovery-nudge-cta">
                <Button className="btn-oxblood rounded-sm">
                  Generate now →
                </Button>
              </Link>
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}
